import { Injectable, Signal, computed, inject } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Data, Router } from '@angular/router';
import { Observable, combineLatest, map, startWith } from 'rxjs';
import { CompassRoute, CompassRouteParent } from '../models/routing';
import { SidenavLink } from '@r1-compass-library/components/sidenav/sidenav.component';
import { injectNavigationEnd } from 'ngxtension/navigation-end';
import { LifecycleRuleset } from '@r1-compass-library/models/lifecycle-rules/lifecycle-ruleset.model';
import { ContentAsset, Play, TemplateCriteria } from '@r1-compass-library/models';

@Injectable({
    providedIn: 'root',
})
export class RoutingService {
    readonly #router = inject(Router);

    currentRoute$: Observable<string> = injectNavigationEnd().pipe(
        startWith(this.#router),
        map(end => end.url),
        takeUntilDestroyed()
    );

    currentRoute: Signal<string> = toSignal(this.currentRoute$, { requireSync: true });

    /**
     * @description The route without params, and just the params
     */
    currentRoutePieces = computed(() => {
        const hasParams = this.currentRoute()?.includes('?');

        return {
            routeWithoutParams: hasParams ? this.currentRoute()?.split('?')[0] : this.currentRoute(),
            routeParams: hasParams ? this.currentRoute()?.split('?')[1] : '',
        };
    });

    /**
     * @example `/${high-level-route}`
     */
    topRoute = computed(() => this.currentRoutePieces().routeWithoutParams?.split('/').slice(0, 2).join('/'));

    /**
     * @example `/${thisPart}?param=X` right after `$topRoute = `/${high-level-route}`
     */
    restOfRoute = computed(() => {
        const restOfNonParams = this.currentRoutePieces().routeWithoutParams?.split('/').slice(-1).join('/') ?? '';
        let restWithOrWithoutParams = '';
        if (this.currentRoutePieces().routeParams) {
            restWithOrWithoutParams = '/' + this.currentRoutePieces().routeParams;
        }
        return '/' + restOfNonParams + restWithOrWithoutParams;
    });

    public assetId: Signal<string | undefined> = computed(() => this.currentRoute()?.split('/').at(2));

    routeDisabledUntilAssetCreated: Signal<boolean> = computed(() => this.assetId() === 'new');

    parentSegment = computed(() => {
        const segments = this.currentRoutePieces().routeWithoutParams?.split('/');
        return segments?.at(segments?.length - 2);
    });

    hideSidenavLinks = computed(() => this.parentSegment() === '');

    addAssetIdToLinks(links: SidenavLink[]) {
        return links.map(link => {
            return { ...link, url: link.url.replace(':id', this.assetId() ?? '') };
        });
    }

    getContainerTitle(activatedRouteData: Observable<Data>, backupTitle: string): Observable<string> {
        return combineLatest([activatedRouteData, this.currentRoute$]).pipe(
            map(([routeData, currentRoute]) => {
                if ((routeData as CompassRouteParent)?.children?.length) {
                    return (
                        (
                            (routeData as CompassRouteParent)?.children?.find(
                                child => currentRoute.split('/').at(-1) === child.path
                            ) as CompassRoute
                        )?.containerTitle ?? backupTitle
                    );
                } else {
                    return (routeData as CompassRoute)?.containerTitle ?? backupTitle;
                }
            })
        );
    }

    public routeToNewlyCreatedEntity(
        entity: LifecycleRuleset | ContentAsset | TemplateCriteria | Play | undefined,
        entityId: 'new' | string | null,
        activatedRoute: ActivatedRoute
    ) {
        if (entityId === 'new' && entity) {
            this.#router.navigate([`../../${entity?.id}/definition`], {
                relativeTo: activatedRoute,
            });
        }
    }
}
